import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import EditableName from '../EditableName';
import {
  formatRUT,
  formatProcessingTime,
  formatProcessingMethods,
} from '../../utils/driveFormatters';
import { CATEGORY_LABELS } from '../../constants/driveConstants';
import type { ColumnConfig } from '../../hooks/useColumnManager';
import type { TableItem } from '../../services/driveService';
import type { Document as ApiDocument } from '../../services/api';

interface DriveTableCellProps {
  column: ColumnConfig;
  item: TableItem;
  processingSummary?: string;
  privateDocuments: ApiDocument[];
  sharedDocuments: ApiDocument[];
  setPrivateDocuments: React.Dispatch<React.SetStateAction<ApiDocument[]>>;
  setSharedDocuments: React.Dispatch<React.SetStateAction<ApiDocument[]>>;
}

export const DriveTableCell: React.FC<DriveTableCellProps> = ({
  column,
  item,
  processingSummary,
  privateDocuments,
  sharedDocuments,
  setPrivateDocuments,
  setSharedDocuments,
}) => {
  const { theme } = useTheme();

  switch (column.id) {
    case 'checkbox':
    case 'menu':
      return null; // Handled separately in row rendering

    case 'shortName':
      if (item.type === 'document') {
        const doc = item.data as ApiDocument;
        const companyName = (doc?.company_name || '').split(' - ')[0].trim();
        const docType = doc?.document_type || item.documentType || '';
        const period = doc?.document_period || item.documentPeriod || '';
        const parts = [companyName, docType, period].filter((p) => p);
        const concatenated = parts.length > 0 ? parts.join('_') : '-';
        return (
          <div className={`text-sm ${theme.colors.text.primary} truncate`} title={concatenated}>
            {concatenated}
          </div>
        );
      }
      return (
        <div className={`text-sm ${theme.colors.text.secondary} italic whitespace-nowrap`}>-</div>
      );

    case 'title':
      if (item.type === 'document') {
        const doc = item.data as ApiDocument;
        const originalName = doc?.original_filename || doc?.name || '';
        return (
          <div
            className={`text-sm ${theme.colors.text.primary} truncate`}
            title={processingSummary ? `${originalName} • ${processingSummary}` : originalName}
          >
            {originalName}
          </div>
        );
      }
      return (
        <div className={`text-sm ${theme.colors.text.primary} truncate`} title={item.name}>
          {item.name}
        </div>
      );

    case 'category':
      return (
        <span
          className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${theme.colors.bg.tertiary} ${theme.colors.text.primary}`}
        >
          {CATEGORY_LABELS[item.category]}
        </span>
      );

    case 'company':
      const fullName = item.companyName || '';
      const companyNameOnly = fullName.split(' - ')[0].trim();
      return (
        <div className={`text-xs ${theme.colors.text.primary} truncate`} title={item.companyName}>
          {companyNameOnly || '-'}
        </div>
      );

    case 'shortCompany':
      if (item.type === 'document') {
        const doc = item.data as ApiDocument;
        const fullCompanyName = doc?.company_name || '';
        const shortCompany = fullCompanyName.split(' - ')[0].trim();
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <EditableName
              value={shortCompany}
              documentId={item.id}
              field="company_name"
              onUpdate={(newCompany) => {
                const foundDoc = privateDocuments.find((d) => d.id === item.id);
                if (foundDoc) {
                  setPrivateDocuments((prev) =>
                    prev.map((d) =>
                      d.id === item.id ? { ...d, company_name: newCompany.trim() } : d
                    )
                  );
                }
                const foundSharedDoc = sharedDocuments.find((d) => d.id === item.id);
                if (foundSharedDoc) {
                  setSharedDocuments((prev) =>
                    prev.map((d) =>
                      d.id === item.id ? { ...d, company_name: newCompany.trim() } : d
                    )
                  );
                }
              }}
              className="text-xs"
              placeholder="Click para agregar empresa"
              maxLength={100}
            />
          </div>
        );
      }
      return (
        <div className={`text-xs ${theme.colors.text.primary} truncate`} title={item.companyName}>
          {item.companyName}
        </div>
      );

    case 'docType':
      return (
        <div className={`text-xs ${theme.colors.text.primary} truncate`} title={item.documentType}>
          {item.documentType}
        </div>
      );

    case 'period':
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <EditableName
            value={item.documentPeriod || ''}
            documentId={item.id}
            field="document_period"
            onUpdate={(newPeriod) => {
              const foundDoc = privateDocuments.find((d) => d.id === item.id);
              if (foundDoc) {
                setPrivateDocuments((prev) =>
                  prev.map((d) => (d.id === item.id ? { ...d, document_period: newPeriod } : d))
                );
              }
              const foundSharedDoc = sharedDocuments.find((d) => d.id === item.id);
              if (foundSharedDoc) {
                setSharedDocuments((prev) =>
                  prev.map((d) => (d.id === item.id ? { ...d, document_period: newPeriod } : d))
                );
              }
            }}
            className="text-xs"
            placeholder="Ej: 2T2025"
            maxLength={20}
          />
        </div>
      );

    case 'periodOriginal':
      return (
        <div
          className={`text-xs ${theme.colors.text.primary} truncate`}
          title={item.originalPeriod || item.documentPeriod || ''}
        >
          {item.originalPeriod || item.documentPeriod || '-'}
        </div>
      );

    case 'code':
      return (
        <div className={`text-xs ${theme.colors.text.primary} truncate`} title={item.documentCode}>
          {formatRUT(item.documentCode)}
        </div>
      );

    case 'sector':
      if (item.type === 'document') {
        const sectorDoc = item.data as ApiDocument;
        const sector = sectorDoc?.shared_category || '';
        return (
          <div className={`text-xs ${theme.colors.text.primary} truncate`} title={sector}>
            {sector || '-'}
          </div>
        );
      }
      return <div className={`text-xs ${theme.colors.text.secondary}`}>-</div>;

    case 'published':
      if (item.type === 'document') {
        const pubDoc = item.data as ApiDocument;
        const publishedAt = pubDoc?.published_at;
        if (publishedAt) {
          const d = new Date(publishedAt);
          const formatted = d.toLocaleDateString('es-CL', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });
          return (
            <div className={`text-xs ${theme.colors.text.primary} truncate`} title={publishedAt}>
              {formatted}
            </div>
          );
        }
      }
      return <div className={`text-xs ${theme.colors.text.secondary}`}>-</div>;

    case 'date':
      return <div className={`text-xs ${theme.colors.text.primary} truncate`}>{item.date}</div>;

    case 'size':
      return <div className={`text-xs ${theme.colors.text.primary} truncate`}>{item.size}</div>;

    case 'pages':
      return <div className={`text-xs ${theme.colors.text.primary} text-center`}>{item.pages}</div>;

    case 'chunks':
      return (
        <div className={`text-xs ${theme.colors.text.primary} text-center`}>{item.chunks}</div>
      );

    case 'processing':
      const processingTimeLabel = formatProcessingTime(item.processingTimeSeconds);
      const processingMethodsLabel = formatProcessingMethods(item.processingMethods);
      // Debug log removido - estaba generando miles de logs innecesarios
      // Si necesitas debuggear, usar logger.debug() condicionalmente solo en desarrollo
      return (
        <div
          className={`text-xs ${theme.colors.text.primary} truncate`}
          title={processingMethodsLabel ? `Métodos: ${processingMethodsLabel}` : undefined}
        >
          {processingTimeLabel ?? '-'}
        </div>
      );

    case 'status':
      return (
        <div
          className={`text-xs truncate ${
            item.status === 'Completado'
              ? 'text-nb-success'
              : item.status === 'Procesando'
                ? 'text-nb-accent'
                : item.status === 'Error'
                  ? 'text-nb-error'
                  : theme.colors.text.primary
          }`}
        >
          {item.status}
        </div>
      );

    default:
      return null;
  }
};
